import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const SESSION_KEY = "dw_chat_session";
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = crypto.randomUUID ? crypto.randomUUID() : String(Date.now());
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

export const ChatWidget = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);
  const session = useRef(getSession());

  useEffect(() => { api.get("/settings").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!cfg || cfg.chat_enabled === false) return null;

  const biz = cfg.business_name || "DealsWagon";
  const waDigits = (cfg.whatsapp_number || "").replace(/[^\d]/g, "");
  const waUrl = waDigits ? `https://wa.me/${waDigits}` : "";

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.current, message: text }),
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() || "";
        for (const c of chunks) {
          const line = c.replace(/^data: /, "").trim();
          if (!line) continue;
          let data;
          try { data = JSON.parse(line); } catch { continue; }
          if (data.delta) {
            setMessages((m) => {
              const n = [...m];
              n[n.length - 1] = { role: "assistant", content: n[n.length - 1].content + data.delta };
              return n;
            });
          }
          if (data.error) {
            setMessages((m) => {
              const n = [...m];
              n[n.length - 1] = { role: "assistant", content: data.error };
              return n;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return n;
      });
    }
    setBusy(false);
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          aria-label="Open Ask DealsWagon chat"
          className="fixed bottom-5 right-5 z-[90] h-14 w-14 rounded-full bg-brand-red text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-[90] bg-white border border-slate-200 shadow-2xl flex flex-col
                     inset-x-0 bottom-0 h-[80vh] rounded-t-2xl
                     sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[560px] sm:w-[380px] sm:rounded-2xl"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-brand-navy text-white sm:rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-yellow" />
              <div>
                <div className="font-heading text-base leading-tight">Ask {biz}</div>
                <div className="text-[11px] text-white/70 leading-tight">In-stock help, instantly</div>
              </div>
            </div>
            <button data-testid="chat-close-btn" onClick={() => setOpen(false)} aria-label="Close chat"
              className="p-1.5 rounded-full hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-sm text-slate-500">
                <p className="font-semibold text-brand-navy">Hi! 👋 Looking for something?</p>
                <p className="mt-1">Ask me about what's in stock, prices, or drive-through pickup. I only answer from our live catalog.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user" ? "bg-brand-navy text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : "")}
                </div>
              </div>
            ))}
          </div>

          {waUrl && (
            <a
              data-testid="chat-whatsapp-btn"
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-4 mb-2 mt-2 inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[#25D366] text-white font-bold text-sm hover:brightness-105"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          )}

          <div className="p-3 border-t border-slate-200 bg-white sm:rounded-b-2xl">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about a product..."
                className="flex-1 resize-none rounded-2xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy max-h-24"
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-full bg-brand-red text-white flex items-center justify-center disabled:opacity-50"
                aria-label="Send message"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
